import { isNumber } from "@/utils/utils";
import { Card, Typography } from "antd";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import AntIcon from "../../../../../../common/components/icons/AntIcon";
import { formatLocaleDate } from "../../../../../../common/utils/formatUtils";
import styles from "./SquareGrid.module.scss";

const calculateBestColumnsAndSize = (
  containerWidth: number,
  containerHeight: number,
  count: number,
  gap: number,
  minColumns: number,
) => {
  let columns = minColumns;
  let prevSize = 0;
  let bestSize = 0;
  let bestColumns = columns;
  let finalSize: number | undefined = undefined;

  while (columns <= count) {
    const rows = Math.ceil(count / columns);
    const totalHorizontalGap = gap * (columns - 1);
    const totalVerticalGap = gap * (rows - 1);

    const widthAvailable = finalSize ? (columns + 1) * finalSize : containerWidth;
    const maxSizeByWidth = (widthAvailable - totalHorizontalGap) / columns;
    const maxSizeByHeight = (containerHeight - totalVerticalGap) / (rows + 0.5);

    const size = Math.floor(Math.min(maxSizeByWidth, maxSizeByHeight));

    if (size <= 120 && !finalSize) {
      finalSize = 120;
      columns++;
      continue;
    }

    if (size < prevSize && prevSize !== 0) {
      break;
    }

    bestSize = size;
    bestColumns = columns;
    prevSize = size;
    columns++;
  }

  return { columns: bestColumns, squareSize: bestSize };
};

type Item = {
  id: string;
  name: string;
  completedDate?: string;
};

type Props = {
  items: Item[];
};

const GAP_SPACE = 8;

export const SquareGrid = ({ items }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [squareSize, setSquareSize] = useState(0);
  const [columns, setColumns] = useState(3);

  const count = items.length;
  const minColumns = Math.min(count, 3);

  const updateData = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const sizeData = calculateBestColumnsAndSize(
      containerRef.current.clientWidth,
      containerRef.current.clientHeight,
      count,
      GAP_SPACE,
      minColumns,
    );

    if (sizeData?.columns) {
      setColumns(sizeData.columns);
    }

    if (sizeData && isNumber(sizeData?.squareSize)) {
      setSquareSize(sizeData.squareSize);
    }
  }, [count]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateData();
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [updateData, count]);

  return (
    <div
      ref={containerRef}
      style={{
        gridTemplateColumns: `repeat(${columns}, ${squareSize}px)`,
        gridAutoRows: `${squareSize}px`,
        gap: GAP_SPACE,
        width: "100%",
        overflowX: "auto",
        justifyContent: count < 6 ? "center" : undefined,
      }}
      className={styles.grid}
    >
      {items.map((item, index) => (
        <Card
          key={item.id}
          className={classNames(styles.card, { [styles.cardCompleted as string]: !!item.completedDate })}
          style={{
            width: squareSize,
            height: squareSize,
            transform: (index % columns) % 2 === 1 ? `translateY(${squareSize / 2}px)` : undefined,
          }}
          styles={{ body: { padding: 10, width: "100%" } }}
        >
          <div style={{ textAlign: "center" }}>
            {item.completedDate ? <AntIcon style={{ fontSize: 24 }} type="trophy" color="yellow" /> : undefined}

            <div style={{ fontWeight: 500, fontSize: 14, lineHeight: "20px" }}>
              <Typography.Paragraph
                style={{ marginBottom: 0 }}
                className={classNames(styles.text, { [styles.textCompleted as string]: !!item.completedDate })}
                ellipsis={{ rows: 2, tooltip: item.name }}
              >
                {item.name}
              </Typography.Paragraph>

              <i style={{ fontSize: 12 }}>{item.completedDate ? formatLocaleDate(item.completedDate) : undefined}</i>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
